import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, ArrowRight, Building2, Search,
  CheckCircle2, AlertCircle,
} from 'lucide-react';
import { useForm }     from 'react-hook-form';
import { toast }       from 'sonner';
import { z }           from 'zod';

import { Button } from '@/components/ui/button';
import { Input }  from '@/components/ui/input';
import { Label }  from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { companiesApi } from '@/lib/api';
import { QK }           from '@/lib/query-client';

const schema = z.object({
  ruc:             z.string().length(11, 'El RUC debe tener 11 dígitos').regex(/^\d+$/, 'Solo dígitos'),
  razonSocial:     z.string().min(2, 'Requerido'),
  regimen:         z.enum(['RUS', 'RER', 'RMT', 'GENERAL']),
  contactNombre:   z.string().trim().min(2, 'Requerido'),
  contactTelefono: z.string().trim().min(7, 'Ingresa un teléfono válido'),
  contactEmail:    z.email('Email inválido').or(z.literal('')).optional(),
});
type Form = z.infer<typeof schema>;

interface CompanyRef { id: string; ruc: string; razonSocial: string; }
interface AxiosErr   { response?: { status?: number; data?: { message?: string } }; }

interface SunatPreview {
  razonSocial?: string;
  estado?:      string;
  tipoPersona?: string;
}

interface StepEmpresaProps {
  onNext: (company: CompanyRef) => void;
  onBack: () => void;
}

export function StepEmpresa({ onNext, onBack }: StepEmpresaProps) {
  const queryClient = useQueryClient();

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: { regimen: 'GENERAL' },
  });

  const ruc = watch('ruc') ?? '';

  // ── Lookup (preview) ───────────────────────────────────────────────────────
  const lookup = useMutation({
    mutationFn: (r: string) => companiesApi.lookupRuc(r),
    onSuccess: (res) => {
      if (!res.data.success || !res.data.data) {
        toast.error(res.data.message ?? 'RUC no encontrado en SUNAT');
        return;
      }
      const d = res.data.data;
      if (d.razonSocial) setValue('razonSocial', d.razonSocial);
      if (d.email)       setValue('contactEmail', d.email);
      toast.success('Datos cargados desde SUNAT');
    },
    onError: () => toast.error('No se pudo consultar el RUC. Ingresa la razón social manualmente.'),
  });

  const sunatPreview = lookup.data?.data?.data as SunatPreview | null | undefined;

  // ── Create ──────────────────────────────────────────────────────────────────
  const create = useMutation({
    mutationFn: (data: Form) =>
      companiesApi.create({
        ruc:             data.ruc,
        razonSocial:     data.razonSocial,
        regimen:         data.regimen,
        contactNombre:   data.contactNombre,
        contactTelefono: data.contactTelefono,
        contactEmail:    data.contactEmail || undefined,
      }).then(r => r.data as CompanyRef),

    onSuccess: async (company) => {
      await queryClient.invalidateQueries({ queryKey: QK.companies() });
      onNext(company);
    },

    onError: (err: unknown) => {
      const e   = err as AxiosErr;
      const msg = e.response?.data?.message ?? '';
      if (e.response?.status === 409) {
        toast.error(
          msg.toLowerCase().includes('ruc')
            ? 'Este RUC ya está registrado en tu estudio. Puedes verlo en el panel Empresas.'
            : msg || 'Conflicto al crear la empresa.',
        );
      } else {
        toast.error(msg || 'No se pudo registrar la empresa. Intenta de nuevo.');
      }
    },
  });

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eaf2fa]">
          <Building2 className="h-5 w-5 text-[#1f5d86]" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">Registra tu primera empresa</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Ingresa el RUC y usa el botón de búsqueda para traer los datos desde SUNAT.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(d => { create.mutate(d); })} className="space-y-4">

        {/* RUC + botón buscar */}
        <div className="space-y-1.5">
          <Label htmlFor="ruc">RUC <span className="text-destructive">*</span></Label>
          <div className="flex gap-2">
            <Input
              id="ruc"
              placeholder="20123456789"
              maxLength={11}
              className="font-mono tracking-wider text-base flex-1"
              {...register('ruc')}
            />
            <Button
              type="button"
              variant="outline"
              className="shrink-0 gap-1.5 px-3 border-[#1f5d86]/30 text-[#1f5d86] hover:bg-[#eaf2fa]"
              disabled={ruc.length !== 11 || lookup.isPending}
              onClick={() => { lookup.mutate(ruc); }}
            >
              {lookup.isPending
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Search className="h-4 w-4" />
              }
              <span className="hidden sm:inline text-sm">Buscar</span>
            </Button>
          </div>
          {errors.ruc && (
            <p className="flex items-center gap-1 text-xs text-destructive">
              <AlertCircle className="h-3 w-3" />
              {errors.ruc.message}
            </p>
          )}
        </div>

        {/* SUNAT result card */}
        {sunatPreview?.razonSocial && (
          <div className="flex items-start gap-3 rounded-xl border border-[#1d9e75]/30 bg-[#1d9e75]/5 p-3">
            <CheckCircle2 className="h-4 w-4 text-[#1d9e75] shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-semibold text-sm text-foreground truncate">{sunatPreview.razonSocial}</p>
              {sunatPreview.estado && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {sunatPreview.estado}
                  {sunatPreview.tipoPersona ? ` · ${sunatPreview.tipoPersona}` : ''}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Razón social */}
        <div className="space-y-1.5">
          <Label htmlFor="razonSocial">
            Razón social <span className="text-destructive">*</span>
          </Label>
          <Input
            id="razonSocial"
            placeholder="Se completa al buscar el RUC"
            {...register('razonSocial')}
          />
          {errors.razonSocial && (
            <p className="text-xs text-destructive">{errors.razonSocial.message}</p>
          )}
        </div>

        {/* Régimen */}
        <div className="space-y-1.5">
          <Label>Régimen tributario</Label>
          <Select defaultValue="GENERAL" onValueChange={v => { setValue('regimen', v as Form['regimen']); }}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="RUS">Nuevo RUS</SelectItem>
              <SelectItem value="RER">Régimen Especial (RER)</SelectItem>
              <SelectItem value="RMT">Mype Tributario (RMT)</SelectItem>
              <SelectItem value="GENERAL">Régimen General</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Contacto principal */}
        <div className="rounded-xl border border-dashed border-[#1f5d86]/25 bg-[#eaf2fa]/30 p-4 space-y-3">
          <p className="text-xs font-semibold text-[#1f5d86] uppercase tracking-wide">
            Contacto principal de la empresa
          </p>
          <p className="text-xs text-muted-foreground -mt-1">
            Nombre y teléfono a quien se enviarán las alertas de WhatsApp y email.
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="contactNombre">
              Nombre del contacto <span className="text-destructive">*</span>
            </Label>
            <Input
              id="contactNombre"
              placeholder="Ej. Juan Pérez García"
              {...register('contactNombre')}
            />
            {errors.contactNombre && (
              <p className="text-xs text-destructive">{errors.contactNombre.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contactTelefono">
              Teléfono / WhatsApp <span className="text-destructive">*</span>
            </Label>
            <Input
              id="contactTelefono"
              type="tel"
              placeholder="987654321"
              {...register('contactTelefono')}
            />
            {errors.contactTelefono && (
              <p className="text-xs text-destructive">{errors.contactTelefono.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contactEmail">Correo electrónico</Label>
            <Input
              id="contactEmail"
              type="email"
              placeholder="contacto@empresa.pe"
              {...register('contactEmail')}
            />
            {errors.contactEmail && (
              <p className="text-xs text-destructive">{errors.contactEmail.message}</p>
            )}
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onBack} className="h-11">
            Atrás
          </Button>
          <Button
            type="submit"
            className="flex-1 h-11 gap-2"
            style={{ background: 'linear-gradient(135deg, #1f5d86 0%, #2980b9 100%)' }}
            disabled={create.isPending}
          >
            {create.isPending
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Creando...</>
              : <>Crear empresa <ArrowRight className="h-4 w-4" /></>
            }
          </Button>
        </div>
      </form>
    </div>
  );
}
